import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'attributionSourcesOnly'
	| 'attributionExpertsOnly'
	| 'attributionSourcesAndExperts'
	| 'attributionAnswerSourcesOnly'
	| 'attributionAnswerExpertsOnly'
	| 'attributionAnswerSourcesAndExperts'
	| 'sourcesLink'
	| 'expertContributorsLink'
	| 'sourcesPanelHeading'
	| 'sourcesPanelDescription'
	| 'expertsPanelHeading'
	| 'expertsPanelDescription'
	| 'closePanel'
	| 'viewExpert';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description: string }> =
	defineMessages({
		attributionSourcesOnly: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionSourcesOnly',
			defaultMessage: 'Generated from trusted {sources}.',
			description:
				'Inline attribution shown above an AI answer when it was generated from trusted sources only. {sources} is a clickable link reading "sources".',
		},
		attributionExpertsOnly: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionExpertsOnly',
			defaultMessage: 'Generated from {expertContributors}.',
			description:
				'Inline attribution shown above an AI answer when it was generated with input from expert contributors only. {expertContributors} is a clickable link reading "expert contributors".',
		},
		attributionSourcesAndExperts: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionSourcesAndExperts',
			defaultMessage: 'Generated from trusted {sources} and {expertContributors}.',
			description:
				'Inline attribution shown above an AI answer when it was generated from trusted sources and expert contributors. {sources} and {expertContributors} are clickable links.',
		},
		attributionAnswerSourcesOnly: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionAnswerSourcesOnly',
			defaultMessage: 'generated from trusted {sources}.',
			description:
				'SAIN variant: continuation of attribution line after the bold "Answer" label, when generated from trusted sources only. {sources} is a clickable link.',
		},
		attributionAnswerExpertsOnly: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionAnswerExpertsOnly',
			defaultMessage: 'generated from {expertContributors}.',
			description:
				'SAIN variant: continuation of attribution line after the bold "Answer" label, when generated with expert contributors only. {expertContributors} is a clickable link.',
		},
		attributionAnswerSourcesAndExperts: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.attributionAnswerSourcesAndExperts',
			defaultMessage: 'generated from trusted {sources} and {expertContributors}.',
			description:
				'SAIN variant: continuation of attribution line after the bold "Answer" label, when generated from trusted sources and expert contributors. {sources} and {expertContributors} are clickable links.',
		},
		sourcesLink: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.sourcesLink',
			defaultMessage: 'sources',
			description:
				'Clickable link text within the attribution line that opens the trusted sources panel.',
		},
		expertContributorsLink: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.expertContributorsLink',
			defaultMessage: 'expert contributors',
			description:
				'Clickable link text within the attribution line that opens the expert contributors panel.',
		},
		sourcesPanelHeading: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.sourcesPanelHeading',
			defaultMessage: '{count, plural, one {# trusted source} other {# trusted sources}}',
			description: 'Heading of the trusted sources panel. {count} is the number of sources.',
		},
		sourcesPanelDescription: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.sourcesPanelDescription',
			defaultMessage:
				'We use sources that are widely viewed, recently updated, and written by experts in your org.',
			description: 'Subtitle explaining why the listed sources are considered trusted.',
		},
		expertsPanelHeading: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.expertsPanelHeading',
			defaultMessage: '{count, plural, one {# expert contributor} other {# expert contributors}}',
			description:
				'Heading of the expert contributors panel. {count} is the number of expert contributors.',
		},
		expertsPanelDescription: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.expertsPanelDescription',
			defaultMessage:
				'These experts regularly contribute to similar topics and have domain knowledge.',
			description: 'Subtitle explaining why the listed people are considered expert contributors.',
		},
		closePanel: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.closePanel',
			defaultMessage: 'Close',
			description: 'Accessible label for the button that dismisses an attribution detail panel.',
		},
		viewExpert: {
			id: 'rovo-platform-ui-components.authoritative-answer-attribution.viewExpert',
			defaultMessage: 'View {name}',
			description:
				'Accessible label for the button that navigates to an expert contributor profile. {name} is the expert name.',
		},
	});

export default messages;
